import React, { useMemo } from 'react';
import { RuleEditor } from './modules/ruleEditor';
import { useNavigate, useParams } from 'react-router-dom';
import { useScoreModel } from '../../model/context/scoreModelContext';
import { useController, useResource, useSuspense } from 'rest-hooks';
import { PolicyRuleResource } from '../../model/resources/policyRuleResource';
import { PolicyRule } from '../../model/interfaces/policyRule';
import {
  scoreModel2PolicyRuleFactory,
  ScoreModel2PolicyRuleResource
} from '../../model/resources/scoreModel2PolicyRulesResource';
import { ScoreModel2PolicyRule } from '../../model/interfaces/scoreModel2PolicyRule';
import { FormattedMessage, useIntl } from 'react-intl';
import { useActionModal } from '../../modules/actionModal';
import { useSnackbar } from 'notistack';
import { Stack, Typography } from '@mui/material';
import { CreatingNewRuleProps } from './rulesPage';

export const EditorRulesPage = ({
  isCreatingNewRule,
  setIsCreatingNewRule,
  showAllRules,
  setShowAllRules,
  hasUnsavedChanges,
  setHasUnsavedChanges
}: CreatingNewRuleProps) => {
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const { openActionModal } = useActionModal();
  const navigate = useNavigate();
  const { ruleid } = useParams();
  const { fetch } = useController();
  const { scoreModel, refresh } = useScoreModel();
  const allRules = useSuspense(PolicyRuleResource.list());

  const rules = useMemo(
    () => (showAllRules ? allRules : scoreModel?.policyRules ?? []),
    [showAllRules, allRules, scoreModel?.policyRules]
  );

  const selectedRuleId = useMemo(
    () => (Number.isNaN(Number(ruleid)) ? undefined : Number(ruleid)),
    [ruleid]
  );

  const selectedRule = useMemo(
    () => rules.find((rule) => rule.policyRuleId === selectedRuleId) ?? null,
    [rules, selectedRuleId]
  );

  const listOfScoreModel2PolicyRules = useResource(
    ScoreModel2PolicyRuleResource.list(),
    selectedRule?.policyRuleId
      ? {
          policyRuleId: selectedRule?.policyRuleId
        }
      : null
  );

  const otherScoreModelsWithSelectedRule = useMemo(
    () =>
      listOfScoreModel2PolicyRules
        ?.filter(
          (scoreModel2PolicyRule) => scoreModel2PolicyRule.scoreModelId !== scoreModel?.scoreModelId
        )
        .map(
          (scoremodel2policyRule: ScoreModel2PolicyRule) => scoremodel2policyRule?.scoreModel?.name
        ),
    [listOfScoreModel2PolicyRules, scoreModel]
  );

  //Save a new Rule
  const createRule = async (rule: PolicyRule) => {
    try {
      const newRule: PolicyRule = await fetch(
        PolicyRuleResource.create(),
        { scoreModelId: scoreModel.scoreModelId, policyRuleId: rule.policyRuleId },
        rule
      ).then((result) => {
        enqueueSnackbar(
          intl.formatMessage({
            id: 'snackbar.rule.create.success',
            defaultMessage: 'New rule created'
          }),
          { variant: 'success' }
        );
        return result;
      });

      const scoreModel2PolicyRule = scoreModel2PolicyRuleFactory(scoreModel, newRule);
      await fetch(ScoreModel2PolicyRuleResource.create(), scoreModel2PolicyRule).then((e) => {
        enqueueSnackbar(
          intl.formatMessage({
            id: 'snackbar.scoremodel2rule.create.success',
            defaultMessage: 'New rule added to active score model'
          }),
          { variant: 'success' }
        );
      });

      setIsCreatingNewRule(false);
      setHasUnsavedChanges(false);
      navigate(`/rules/${newRule.policyRuleId}`, { replace: true });
      refresh();
    } catch (e) {
      enqueueSnackbar(
        intl.formatMessage({
          id: 'snackbar.rule.create.error',
          defaultMessage: 'Could not create new rule'
        }),
        { variant: 'error' }
      );
    }
  };

  const updateRule = async (rule: PolicyRule) => {
    try {
      await fetch(PolicyRuleResource.update(), { policyRuleId: rule.policyRuleId }, rule);
      setHasUnsavedChanges(false);
      enqueueSnackbar(
        intl.formatMessage({
          id: 'snackbar.rule.update.success',
          defaultMessage: 'Rule updated'
        }),
        { variant: 'success' }
      );
    } catch (e) {
      enqueueSnackbar(
        intl.formatMessage({
          id: 'snackbar.rule.update.error',
          defaultMessage: 'Could not update rule'
        }),
        { variant: 'error' }
      );
    }
  };

  const deleteRule = async (rule: PolicyRule) => {
    try {
      const id = rule.policyRuleId;
      await fetch(PolicyRuleResource.delete(), { policyRuleId: id });
      enqueueSnackbar(
        intl.formatMessage({
          id: 'snackbar.rule.delete.success',
          defaultMessage: 'Rule deleted'
        }),
        { variant: 'success' }
      );
      setHasUnsavedChanges(false);
      navigate(`/rules`, { replace: true });
    } catch (e) {
      enqueueSnackbar(
        intl.formatMessage({
          id: 'snackbar.rule.delete.error',
          defaultMessage: 'Could not delete rule'
        }),
        { variant: 'error' }
      );
    }
  };

  //Update an exsisting Rule
  const requestUpdate = async (rule: PolicyRule) => {
    if (otherScoreModelsWithSelectedRule && otherScoreModelsWithSelectedRule.length > 0) {
      openActionModal(
        <FormattedMessage
          id="updateTitle"
          defaultMessage="Save {selectedRule}?"
          values={{ selectedRule: selectedRule?.name }}
        />,
        <FormattedMessage
          id="update"
          defaultMessage={
            'Are you sure you want to save {selectedRule}? {selectedRule} is also used in: {otherScoreModels}'
          }
          values={{
            selectedRule: selectedRule?.name,
            otherScoreModels: otherScoreModelsWithSelectedRule
              ? otherScoreModelsWithSelectedRule.join(', ')
              : ''
          }}
        />,
        'Save',
        'Cancel',
        () => updateRule(rule)
      );
    } else {
      updateRule(rule);
    }
  };

  //Delete an exising rule
  const requestDeleteRule = async (rule: PolicyRule) => {
    openActionModal(
      <Typography variant={'h4'}>
        <FormattedMessage
          id="deleteTitle"
          defaultMessage="Delete {selectedRule}?"
          values={{ selectedRule: selectedRule?.name }}
        />{' '}
      </Typography>,
      <Typography variant={'body2'}>
        <FormattedMessage
          id="delete"
          defaultMessage={
            'Are you sure you want to delete {selectedRule}? {selectedRule} is also used in: {otherScoreModels}'
          }
          values={{
            selectedRule: selectedRule?.name,
            otherScoreModels: otherScoreModelsWithSelectedRule
              ? otherScoreModelsWithSelectedRule.join(', ')
              : ''
          }}
        />
      </Typography>,
      'Delete',
      'Cancel',
      () => deleteRule(rule)
    );
  };

  return (
    // Need "height: 100%" in order for overflow-scroller to work
    <Stack padding={0} style={{ height: '100%' }}>
      {isCreatingNewRule ? (
        <RuleEditor
          rule={PolicyRuleResource.fromJS()}
          onSave={(rule) => createRule(rule)}
          onDelete={() => {
            setIsCreatingNewRule(false);
            navigate(`/rules`, { replace: true });
          }}
        />
      ) : selectedRule ? (
        <RuleEditor
          rule={selectedRule}
          setHasUnsavedChanges={(hasUnsavedChanges) => setHasUnsavedChanges(hasUnsavedChanges)}
          onSave={(rule) => requestUpdate(rule)}
          onDelete={(rule) => requestDeleteRule(rule)}
        />
      ) : null}
    </Stack>
  );
};
